import { Alert, Box, Button, Container, Divider, Typography } from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React from 'react';
import { useNavigate } from 'react-router-dom';

const CONTACT_EMAIL = 'info@nativecoders.se';
const LAST_UPDATED = '2026-04-26';

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 3, '& > *:last-child': { mb: 0 } }}>
    <Typography variant="h6" sx={{ mb: 1 }}>
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const SubHeading = ({ children }) => (
  <Typography variant="legalSubheading" sx={{ mb: 1 }}>{children}</Typography>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 1 }}>
    {items.map((item, i) => (
      <Typography key={i} component="li" variant="body1" sx={{ mb: 0.5 }}>
        {item}
      </Typography>
    ))}
  </Box>
);

const Paragraph = ({ children, sx }) => (
  <Typography variant="body1" sx={{ mb: 1, ...sx }}>
    {children}
  </Typography>
);

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <Box data-tc="page" sx={{ minHeight: '100vh', py: 3 }}>
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          data-tc="back"
          sx={{ mb: 2 }}
        >
          Go Back
        </Button>

        <Box data-tc="card" sx={{ borderRadius: 2, p: { xs: 3, md: 5 } }}>
          <Typography variant="h4" data-tc="page-title" sx={{ mb: 0.5 }}>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" data-tc="subtitle" sx={{ mb: 3 }}>
            Native Coders Talent Community (NCTC)
          </Typography>

          <Alert severity="warning" icon={false} sx={{ mb: 3 }}>
            <strong>Effective Date:</strong> The date on which you register for the NCTC system and accept these Terms and Conditions.
          </Alert>

          <Paragraph sx={{ mb: 3 }}>
            These Terms and Conditions ("Terms") govern your access to and use of the Native Coders Talent Community ("NCTC"),
            a platform operated by Native Coders Company Limited and Native Coders Sweden AB ("Native Coders"). By registering
            for and using NCTC, you agree to be bound by these Terms.
          </Paragraph>
          <Paragraph sx={{ mb: 3 }}>
            NCTC is a system operated by Native Coders, which acts as the data controller and service provider for the platform.
          </Paragraph>

          <Section number="1" title="Purpose of NCTC">
            <Paragraph>NCTC is a secure talent, engagement, and information management system used to manage:</Paragraph>
            <BulletList items={[
              'CVs, skills, and professional profiles of candidates',
              'Employee or freelance information, including skills, certifications, project history, and HR-related data',
              'Engagement, assignment, and opportunity-related data',
              'Documents, contracts, and other uploaded materials',
            ]} />
            <Paragraph>The platform supports recruitment, talent matching, workforce management, business operations, and compliance processes.</Paragraph>
            <Paragraph>
              NCTC is currently designed as a talent community focused on women in technology, with the objective of increasing
              participation, visibility, and opportunities in a sector where they remain underrepresented.
            </Paragraph>
            <Paragraph>The scope and functionality of NCTC may be expanded or modified over time.</Paragraph>
          </Section>

          <Section number="2" title="Eligibility">
            <Paragraph>
              NCTC is intended for individuals participating in recruitment, freelance, or employment-related processes managed through NCTC.
              Access may be contingent upon a legitimate engagement or participation process.
            </Paragraph>
            <SubHeading>Eligibility for the Talent Community</SubHeading>
            <Paragraph>Participation in the Talent Community is subject to specific eligibility criteria aligned with its purpose.</Paragraph>
            <BulletList items={[
              'Full registration and participation are currently limited to individuals who identify as women or other underrepresented groups in technology.',
              'Individuals who do not meet this criterion may, where permitted, create a limited profile.',
              'Such profiles will not be included in talent searches, matching services, or client-facing opportunities.',
            ]} />
            <Paragraph>
              This limitation applies specifically to the Talent Community and its associated matching and visibility features
              and reflects the purpose of this initiative.
            </Paragraph>
          </Section>

          <Section number="3" title="Data Submission and Responsibility">
            <Paragraph>
              You are responsible for ensuring that all information you provide is accurate, complete, and truthful.
              By registering, you:
            </Paragraph>
            <BulletList items={[
              'Confirm that the information provided (including information relevant to eligibility) is accurate.',
              'Consent to the processing of your data as described in these Terms.',
            ]} />
            <Paragraph>Providing false or misleading information may result in restriction of access or removal from the platform.</Paragraph>
          </Section>

          <Section number="4" title="Use of Personal Information">
            <Paragraph>Your personal data may be used to:</Paragraph>
            <BulletList items={[
              'Evaluate your suitability for roles, projects, or engagements',
              'Match your skills, experience, and availability with opportunities',
              'Manage your participation in the Talent Community and visibility settings',
              'Support recruitment, onboarding, and workforce management',
              'Administer HR-related processes, including role assignments and reporting structures',
              'Process financial and contractual information (e.g., payments, invoicing)',
              'Maintain compliance with legal, regulatory, and audit requirements',
            ]} />
            <Paragraph>This includes processing of:</Paragraph>
            <BulletList items={[
              'Identity and contact information',
              'Professional and skills data',
              'Availability, preferences, and engagement data',
              'HR and employment-related records',
              'Financial and statutory information',
              'Documents and uploaded content',
            ]} />
            <Paragraph>Registration does not guarantee inclusion in matching or access to opportunities.</Paragraph>
          </Section>

          <Section number="5" title="Sensitive and Financial Data">
            <Paragraph>NCTC may process sensitive categories of data, including:</Paragraph>
            <BulletList items={[
              'Financial information (e.g., bank account details, payment data)',
              'National identifiers and statutory registration numbers',
              'HR and employment-related records',
            ]} />
            <Paragraph>Such data is:</Paragraph>
            <BulletList items={[
              'Processed strictly for legitimate business and legal purposes',
              'Protected through enhanced security and access controls',
              'Accessible only to authorized personnel',
            ]} />
          </Section>

          <Section number="6" title="Third-Party Data (Emergency Contacts)">
            <Paragraph>If you provide personal data relating to third parties (e.g., emergency contacts), you confirm that:</Paragraph>
            <BulletList items={[
              'You have obtained their consent to share such information.',
              'The information is accurate.',
            ]} />
            <Paragraph>Such data will only be used for its intended purpose and handled in accordance with applicable data protection laws.</Paragraph>
          </Section>

          <Section number="7" title="Visibility and Talent Pool Participation">
            <Paragraph>
              You may control certain visibility settings within the platform, including whether your profile is included in talent searches. However:
            </Paragraph>
            <BulletList items={[
              'Visibility is subject to eligibility criteria and platform rules.',
              'NCTC may restrict or override visibility settings based on initiative requirements or operational needs.',
              'Profiles with limited eligibility will not be included in search, matching, or client-facing features regardless of user settings.',
            ]} />
          </Section>

          <Section number="8" title="Uploaded Documents and Content">
            <Paragraph>You may upload documents such as CVs, contracts, certifications, and other materials. By uploading content:</Paragraph>
            <BulletList items={[
              'You retain ownership of your materials.',
              'You grant Native Coders the right to process and use them for recruitment, engagement, operational, and compliance purposes.',
              'You are responsible for ensuring that uploaded content does not violate any confidentiality obligations or third-party rights.',
            ]} />
          </Section>

          <Section number="9" title="Data Protection and Confidentiality">
            <Paragraph>Your data is handled securely and in accordance with applicable data protection laws, including:</Paragraph>
            <BulletList items={[
              'The EU General Data Protection Regulation (GDPR)',
              'The Kenya Data Protection Act',
            ]} />
            <Paragraph>
              NCTC may be accessed by individuals in multiple jurisdictions, including countries across Africa such as Rwanda,
              Nigeria, Tanzania, Ghana, Uganda, and Ethiopia, as well as other regions. In all cases, consistent data protection
              principles aligned with internationally recognized standards of privacy, security, and lawful processing are applied.
            </Paragraph>
            <Paragraph>
              Your data may be processed and stored in countries other than your country of residence, including within the European
              Union and Kenya. Where such cross-border transfers occur, Native Coders ensures that appropriate safeguards are in place.
            </Paragraph>
            <Paragraph>
              Native Coders implements appropriate technical and organizational measures to protect your data from unauthorized access,
              loss, misuse, or disclosure.
            </Paragraph>
          </Section>

          <Section number="10" title="User Account and Access">
            <Paragraph>You will be provided with access credentials upon registration. You are responsible for:</Paragraph>
            <BulletList items={[
              'Maintaining the confidentiality of your credentials.',
              'Notifying Native Coders of any unauthorized access.',
            ]} />
            <Paragraph>Access to data within NCTC is role-based and limited to what is necessary for the user's role or engagement.</Paragraph>
          </Section>

          <Section number="11" title="System Availability and Changes">
            <Paragraph>NCTC is provided without guarantee of uninterrupted access. We may:</Paragraph>
            <BulletList items={[
              'Suspend access for maintenance or upgrades.',
              'Modify features, functionality, or eligibility criteria at any time.',
            ]} />
          </Section>

          <Section number="12" title="Account Deactivation and Retention">
            <Paragraph>
              You may request account deletion at any time by contacting us at{' '}
              <Box component="span" data-tc="contact">{CONTACT_EMAIL}</Box>.
              Accounts may be deactivated due to inactivity, end of participation or engagement, or policy violations.
            </Paragraph>
            <Paragraph>
              Certain data (especially HR, financial, and contractual records) may be retained as required by law.
              Further details are set out in Section 17.
            </Paragraph>
          </Section>

          <Section number="13" title="Referrals and Recommendations">
            <Paragraph>Users and visitors may recommend individuals who meet eligibility criteria. By submitting referrals, you confirm that:</Paragraph>
            <BulletList items={[
              "You have the individual's consent to share their information.",
              'The information is accurate.',
            ]} />
          </Section>

          <Section number="14" title="Modifications to the Terms">
            <Paragraph>
              Native Coders may update these Terms from time to time. Material changes will be communicated, and continued use
              of NCTC constitutes acceptance of the updated Terms.
            </Paragraph>
          </Section>

          <Section number="15" title="Governing Law">
            <Paragraph>These Terms are governed by:</Paragraph>
            <BulletList items={[
              'The laws of Sweden (for users primarily associated with EU operations)',
              'The laws of Kenya (for users primarily associated with African operations)',
            ]} />
          </Section>

          <Section number="16" title="Contact Information">
            <Paragraph>
              For questions or concerns, contact:{' '}
              <Box component="span" data-tc="contact">{CONTACT_EMAIL}</Box>
              <br />
              Native Coders Sweden AB / Native Coders Company Limited
            </Paragraph>
          </Section>

          <Divider sx={{ my: 4 }} />

          <Section number="17" title="Account Deactivation, Deletion, Data Export, and Retention">
            <Paragraph sx={{ mb: 2 }}>
              This section supplements Section 12 and provides detailed information on account lifecycle and data retention.
            </Paragraph>

            <SubHeading>1. Candidates</SubHeading>
            <Paragraph>
              Candidates may delete their accounts at any time using the self-service deletion function available in NCTC.
              Before confirming deletion, candidates have the option to export their profile data (CV, skills, and history)
              in a portable format (e.g., CSV or JSON).
            </Paragraph>
            <Paragraph sx={{ mb: 2 }}>
              Once deleted, candidate personal data will be permanently removed or anonymized, except where limited retention
              is required for legal or regulatory purposes (e.g., audit logs).
            </Paragraph>

            <SubHeading>2. Employees</SubHeading>
            <Paragraph>
              Employees cannot directly delete their accounts. Upon employment termination, HR will archive the account.
              Personal data is retained only for legally required periods and then securely deleted or anonymized.
            </Paragraph>
            <Paragraph sx={{ mb: 2 }}>
              Employees may request a copy of their personal data before account archival by contacting HR or using the
              "Request My Data" feature (if available).
            </Paragraph>

            <SubHeading>3. Subcontractors</SubHeading>
            <BulletList items={[
              'Subcontractors cannot delete their accounts while under an active contract. After completion or termination, the account will be archived by HR or Vendor Management. Archived accounts are inaccessible to the subcontractor but retained for the duration required by contractual, billing, and regulatory obligations (commonly up to seven (7) years).',
              'Subcontractors may request an export of their personal data (profile, CV, skills, and engagement history) before account archival or at any time during an active contract.',
              'Following the statutory retention period, subcontractor data will be securely deleted or anonymized.',
            ]} />

            <SubHeading>4. General</SubHeading>
            <Paragraph>
              Minimal non-personal audit logs (e.g., timestamps of account actions) may be retained for compliance and security monitoring.
            </Paragraph>
          </Section>

          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" data-tc="footer">
            Last Updated: {LAST_UPDATED}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsAndConditions;
